// useRealtimeRefresh — subscribe to Supabase Realtime postgres_changes
// events on one or more tables and call back when any row changes.
//
// Pattern:
//   - Pages that already have a `refresh()` callback (e.g. via useMcp)
//     wire it up like so:
//
//       useRealtimeRefresh([
//         { schema: "samples", table: "library_items" },
//         { schema: "samples", table: "library_item_tags" },
//         { schema: "samples", table: "tags" },
//       ], refresh);
//
//     When anyone in the company inserts/updates/deletes one of those
//     rows, the page re-fetches itself. RLS at the publication layer
//     means clients only receive events for rows they can SELECT — view-
//     only / scoped users still respect their permissions.
//
// Implementation notes:
//   - One channel per hook INVOCATION. The channel name is derived from the
//     table list PLUS a per-instance id (React.useId). It must not be
//     derived from the table list alone: supabase.channel(name) returns the
//     already-existing channel when the name is reused, and calling .on(
//     'postgres_changes', ...) on a channel that has already subscribed
//     throws ("cannot add `postgres_changes` callbacks ... after
//     `subscribe()`"). Two components that mount together with the same
//     bindings — Layout and AppSidebar both read useBadgeCounts() — would
//     otherwise hand the second one a live channel and crash the whole app
//     through the error boundary on every page.
//   - `event: '*'` covers INSERT/UPDATE/DELETE. We don't currently
//     differentiate — the simplest contract is "something changed,
//     refetch". Pages that need patching-without-refetch can listen
//     for the same channel directly.
//   - Throttle the callback so a burst of N row changes (bulk import)
//     fires the refresh ONCE, after the burst settles. Default 250ms.
//   - `refreshOnSubscribe` (opt-in) closes the no-replay window — see below.
//
// THE NO-REPLAY WINDOW (#1040)
//
// postgres_changes does NOT replay. A client only receives changes that
// happen while its channel is joined, so every consumer has a blind window
// between its initial fetch and the channel reaching SUBSCRIBED (the
// websocket connect + join round trip — tens of ms locally, sometimes
// seconds against a remote project on a cold connection). A row that
// changes inside that window is never delivered, and because this hook's
// whole contract is "refetch on change", the page then shows stale data
// until the NEXT change or a reload. The same window reopens on every
// reconnect: supabase-js rejoins after a socket drop (laptop wake, flaky
// network) but replays nothing from the gap.
//
// `refreshOnSubscribe: true` closes it by calling `onChange` once the
// channel reports SUBSCRIBED — on first connect and on every rejoin. It is
// opt-in rather than the default because it costs one extra fetch per mount
// per subscribing component, which only matters for surfaces where missing
// a change until the next one is actually user-visible (badge counts:
// useBadgeCounts.ts). This was the root cause of #1040's intermittent
// badge-counts e2e failure — the spec emitted its notification while the
// socket was still connecting, so the event was dropped and the badge never
// updated, which looked like a flake but was a real (narrow) product gap.
//
// References: https://supabase.com/docs/guides/realtime/postgres-changes

import { useEffect, useId, useRef } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export interface RealtimeBinding {
  schema: string;
  table: string;
  /** Optional row-level filter — e.g. `id=eq.${id}`. Defaults to all. */
  filter?: string;
  /** Default '*' (INSERT|UPDATE|DELETE). Override to scope down. */
  event?: "INSERT" | "UPDATE" | "DELETE" | "*";
}

/** Connection state of one channel, as reported to `onStatusChange`. */
export type RealtimeStatus = "connecting" | "subscribed" | "error";

interface UseRealtimeRefreshOptions {
  /** Coalesce a burst of changes into one refresh. Default 250ms. */
  throttleMs?: number;
  /** Disable the subscription (e.g. while the page is still loading
   *  initial data). Default false. */
  disabled?: boolean;
  /**
   * Optional: called with the channel's connection state as it changes, so a
   * caller can render a genuine "Live" indicator instead of a hardcoded
   * badge. Fired with "connecting" right before `channel.subscribe()` is
   * called, then "subscribed" / "error" from the subscribe callback below.
   * Additive and optional — every existing caller (grep `useRealtimeRefresh(`
   * across src) is unaffected.
   */
  onStatusChange?: (status: RealtimeStatus) => void;
  /**
   * Call `onChange` once as soon as the channel reports SUBSCRIBED — on the
   * first connect AND on every reconnect — so changes that happened while
   * the socket was not joined are picked up. See "THE NO-REPLAY WINDOW" at
   * the top of this file. Default false (one extra fetch per mount is not
   * worth it on a surface where a missed change isn't user-visible).
   */
  refreshOnSubscribe?: boolean;
}

export function useRealtimeRefresh(
  bindings: RealtimeBinding[],
  onChange: () => void,
  options: UseRealtimeRefreshOptions = {},
): void {
  const {
    throttleMs = 250,
    disabled = false,
    onStatusChange,
    refreshOnSubscribe = false,
  } = options;

  // Stash the latest callbacks in refs so re-renders don't tear down the
  // channel. The subscription is keyed off the table list only.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // A stable string key for the channel name + dependency array. Including
  // event/filter so the same page can reuse the hook with different filters
  // without colliding.
  const key = bindings
    .map((b) => `${b.schema}.${b.table}|${b.event ?? "*"}|${b.filter ?? ""}`)
    .sort()
    .join(",");

  // Unique per hook invocation — see the "One channel per hook INVOCATION"
  // note at the top of this file for why the table list alone is not enough.
  const instanceId = useId();

  useEffect(() => {
    if (disabled) return;
    if (bindings.length === 0) return;

    const callOnChange = () => {
      try {
        onChangeRef.current();
      } catch (err) {
        // Hook contract: never throw out of the channel handler.
        console.warn("[useRealtimeRefresh] callback threw:", err);
      }
    };

    let pending: ReturnType<typeof setTimeout> | null = null;
    const fire = () => {
      if (pending != null) return;
      pending = setTimeout(() => {
        pending = null;
        callOnChange();
      }, throttleMs);
    };

    // The subscribe catch-up is ONE event, not a burst, so it runs
    // unthrottled — throttling it would only widen the window in which the
    // surface shows data older than the moment the socket went live. It
    // supersedes any throttled refresh already queued (that refresh would
    // fetch strictly older data).
    const fireNow = () => {
      if (pending != null) {
        clearTimeout(pending);
        pending = null;
      }
      callOnChange();
    };

    let channel: RealtimeChannel = supabase.channel(`rt:${key}:${instanceId}`);

    for (const b of bindings) {
      channel = channel.on(
        // The supabase-js generic for postgres_changes is loose; cast
        // to `any` because the type union doesn't accept dynamic event
        // strings cleanly. The runtime payload is well-defined; we just
        // refetch on any change so we don't need to inspect it.
        "postgres_changes" as never,
        {
          event: b.event ?? "*",
          schema: b.schema,
          table: b.table,
          ...(b.filter ? { filter: b.filter } : {}),
        // The supabase-js v2 .on() generic is a string literal union we
        // can't match without dragging in the full RealtimePostgresFilter
        // type chain. Cast is intentional and isolated to this hook.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        () => {
          fire();
        },
      );
    }

    onStatusChangeRef.current?.("connecting");
    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        onStatusChangeRef.current?.("subscribed");
        if (refreshOnSubscribe) fireNow();
        return;
      }
      if (status === "CHANNEL_ERROR") {
        console.warn(`[useRealtimeRefresh] channel ${key} errored — supabase-js will auto-reconnect`);
        onStatusChangeRef.current?.("error");
        return;
      }
      if (status === "TIMED_OUT" || status === "CLOSED") {
        onStatusChangeRef.current?.("error");
      }
    });

    return () => {
      if (pending != null) clearTimeout(pending);
      void supabase.removeChannel(channel);
    };
    // We deliberately re-subscribe when the binding string changes; the
    // throttle/disabled/refreshOnSubscribe flags are read at subscribe time
    // only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, disabled]);
}
